import { useTranslations } from '@/i18n/translations';
import { Pill } from '@leanin/ui';
import type { PollResultsView, PollView, PublicPollTallyView } from '@leanin/shared';
import { PeopleListModal, type PeopleListTab } from '../people/index';
import { usePollVoters } from '../../lib/queries/feed';

type Props = {
  postId: string;
  /** Options supply the tab labels; `results` the per-option counts. */
  options: PollView['options'];
  results: PollResultsView<PublicPollTallyView>;
  /** The option whose voters are shown; `null` is the All tab. */
  optionId: string | null;
  onOptionChange: (optionId: string | null) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const ALL = 'all';

// "Who voted" sheet for a public poll: an All tab plus one tab per option that
// drew votes, each an infinite roster of that option's voters.
export function PollVotersModal({
  postId,
  options,
  results,
  optionId,
  onOptionChange,
  open,
  onOpenChange,
}: Props) {
  const t = useTranslations('Feed');
  const tCommon = useTranslations('Common');
  const tab = optionId ?? ALL;
  const query = usePollVoters(postId, {
    ...(optionId !== null ? { optionId } : {}),
    enabled: open,
  });

  const labelFor = new Map(options.map((o) => [o.id, o.label]));
  const countFor = (id: string) =>
    results.tallies.find((tally) => tally.optionId === id)?.voteCount ?? 0;
  const tabs: PeopleListTab[] = [
    { id: ALL, label: t('poll.votersAll'), count: results.totalVotes },
    ...options
      .filter((o) => countFor(o.id) > 0)
      .map((o) => ({ id: o.id, label: o.label, count: countFor(o.id) })),
  ];

  return (
    <PeopleListModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('poll.votersTitle')}
      tabs={tabs}
      activeTab={tab}
      onTabChange={(id) => onOptionChange(id === ALL ? null : id)}
      query={query}
      toPerson={(v) => ({
        key: v.userId,
        userId: v.userId,
        name: v.name,
        photoUrl: v.photoUrl,
        sublabel: v.headline,
        // On the All tab each row says which option that person picked; an
        // option tab already implies it.
        ...(tab === ALL
          ? { right: <Pill size="sm">{labelFor.get(v.optionId) ?? ''}</Pill> }
          : {}),
      })}
      loadingLabel={tCommon('status.loading')}
      emptyLabel={t('poll.votersEmpty')}
      // Tallies still count votes from restricted/deactivated accounts, which
      // the roster (like the card's preview) never lists.
      unlistedLabel={(count) => t('poll.votersUnlisted', { count })}
    />
  );
}
